import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { type AnalyticsPeriod, getWeekBounds } from "./analytics.operatingHours";
import {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  EmptyNote,
  DeleteBtn,
  AddSection,
  AddSectionTitle,
  FormField,
  FormLabel,
  FormInput,
  FormRow,
  DatePickerWrapper,
  AddButtonRow,
  CostList,
  CostRow,
  CostInfo,
  CostTitle,
  CostMeta,
  KindToggleRow,
  KindBtn,
  PeriodSelect,
} from "./analytics.customCosts.styles";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomCost {
  id: string;
  title: string;
  amount: number;
  /** "period" = recurring cost; "date" = one-off on a specific calendar day */
  kind: "period" | "date";
  /** Present when kind === "period" */
  period?: "day" | "week" | "month";
  /** ISO "YYYY-MM-DD", present when kind === "date" */
  date?: string;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

const STORAGE_KEY = "analytics_custom_costs";

export const loadCustomCosts = (): CustomCost[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Back-compat: old entries have period but no kind
      return parsed.map((c): CustomCost => ({
        id: c.id,
        title: c.title,
        amount: c.amount,
        kind: c.kind === "date" ? "date" : "period",
        period: c.period,
        date: c.date,
      }));
    }
  } catch {}
  return [];
};

export const saveCustomCosts = (costs: CustomCost[]): void =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(costs));

// ─── Period-aware total ───────────────────────────────────────────────────────

/**
 * Returns the effective total custom cost for the selected analytics period.
 *
 * For "period" costs — same conversion as before:
 *  ┌──────────┬───────────────────────────────┬──────────────────────────────────┐
 *  │ period   │ Day view                      │ Week view                        │
 *  ├──────────┼───────────────────────────────┼──────────────────────────────────┤
 *  │ day      │ amount                        │ amount × 7                       │
 *  │ week     │ amount / 7                    │ amount                           │
 *  │ month    │ amount / daysInMonth          │ amount × 7 / daysInMonth         │
 *  └──────────┴───────────────────────────────┴──────────────────────────────────┘
 *
 * For "date" costs — filtered by the analytics window (day: exact date match;
 * week: falls within Mon–Sun of selectedDate).
 */
export const getCustomCostsTotal = (
  costs: CustomCost[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): number => {
  const daysInMonth = selectedDate.lengthOfMonth();
  const { weekStart, weekEnd } =
    period === "week" ? getWeekBounds(selectedDate) : { weekStart: null, weekEnd: null };

  return costs.reduce((sum, cost) => {
    const kind = cost.kind ?? "period";

    if (kind === "date") {
      if (!cost.date) return sum;
      if (period === "day") {
        return cost.date === selectedDate.toString() ? sum + cost.amount : sum;
      }
      // week view
      const d = LocalDate.parse(cost.date);
      return weekStart &&
        weekEnd &&
        !d.isBefore(weekStart) &&
        !d.isAfter(weekEnd)
        ? sum + cost.amount
        : sum;
    }

    // period cost
    if (period === "day") {
      if (cost.period === "day") return sum + cost.amount;
      if (cost.period === "week") return sum + cost.amount / 7;
      return sum + cost.amount / daysInMonth; // month
    }
    // week view
    if (cost.period === "day") return sum + cost.amount * 7;
    if (cost.period === "week") return sum + cost.amount;
    return sum + (cost.amount * 7) / daysInMonth; // month
  }, 0);
};

// ─── Side pane ────────────────────────────────────────────────────────────────

interface CustomCostsPaneProps {
  initial: CustomCost[];
  open: boolean;
  /** Pre-fills the date picker when adding a one-time cost */
  selectedDate: LocalDate;
  onSave: (costs: CustomCost[]) => void;
  onClose: () => void;
}

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const CustomCostsPane = (props: CustomCostsPaneProps) => {
  const { t } = useTranslation();
  const [costs, setCosts] = useState<CustomCost[]>(props.initial);
  const [newTitle, setNewTitle] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newKind, setNewKind] = useState<"period" | "date">("period");
  const [newPeriod, setNewPeriod] = useState<"day" | "week" | "month">("day");
  const [newDate, setNewDate] = useState<LocalDate>(props.selectedDate);

  // Reset local state on the false→true transition of props.open.
  // Using a ref to track the previous open value means we can list all
  // consumed props as deps without triggering a reset while the pane is
  // already open (e.g. parent re-renders with a new selectedDate mid-session).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) {
      setCosts(props.initial);
      setNewTitle("");
      setNewAmount("");
      setNewKind("period");
      setNewPeriod("day");
      setNewDate(props.selectedDate);
    }
    wasOpen.current = props.open;
  }, [props.open, props.initial, props.selectedDate]);

  const handleAdd = () => {
    const amount = parseFloat(newAmount.replace(",", "."));
    if (!newTitle.trim() || !Number.isFinite(amount) || amount <= 0) return;
    setCosts((prev) => [
      ...prev,
      newKind === "date"
        ? {
            id: generateId(),
            title: newTitle.trim(),
            amount,
            kind: "date",
            date: newDate.toString(),
          }
        : {
            id: generateId(),
            title: newTitle.trim(),
            amount,
            kind: "period",
            period: newPeriod,
          },
    ]);
    setNewTitle("");
    setNewAmount("");
    // keep kind/period/date — user likely adds a sequence of similar costs
  };

  const handleDelete = (id: string) =>
    setCosts((prev) => prev.filter((c) => c.id !== id));

  const handleSave = () => {
    saveCustomCosts(costs);
    props.onSave(costs);
  };

  return createPortal(
    <>
      <Backdrop $open={props.open} onClick={props.onClose} />
      <Pane $open={props.open}>
        <PaneHeader>
          <PaneTitle>{t("CompanyAnalytics.CustomCosts")}</PaneTitle>
          <CloseBtn onClick={props.onClose} aria-label="Close">
            ✕
          </CloseBtn>
        </PaneHeader>
        <PaneDescription>
          {t("CompanyAnalytics.CustomCostsDescription")}
        </PaneDescription>

        {/* ── Existing costs list ── */}
        <CostList>
          {costs.length === 0 ? (
            <EmptyNote>{t("CompanyAnalytics.NoCustomCosts")}</EmptyNote>
          ) : (
            costs.map((cost) => {
              const kind = cost.kind ?? "period";
              const meta =
                kind === "date" && cost.date
                  ? (() => {
                      const d = LocalDate.parse(cost.date);
                      return `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}/${d.year()}`;
                    })()
                  : cost.period === "day"
                    ? t("CompanyAnalytics.PerDay")
                    : cost.period === "week"
                      ? t("CompanyAnalytics.PerWeek")
                      : t("CompanyAnalytics.PerMonth");
              return (
                <CostRow key={cost.id}>
                  <CostInfo>
                    <CostTitle>{cost.title}</CostTitle>
                    <CostMeta>
                      {Math.round(cost.amount).toLocaleString("sv-SE")} kr
                      {" · "}
                      {meta}
                    </CostMeta>
                  </CostInfo>
                  <DeleteBtn
                    onClick={() => handleDelete(cost.id)}
                    aria-label={t("CompanyAnalytics.DeleteCost")}
                  >
                    ✕
                  </DeleteBtn>
                </CostRow>
              );
            })
          )}
        </CostList>

        {/* ── Add new cost form ── */}
        <AddSection>
          <AddSectionTitle>{t("CompanyAnalytics.AddCost")}</AddSectionTitle>
          <FormField>
            <FormLabel>{t("CompanyAnalytics.CostTitle")}</FormLabel>
            <FormInput
              type="text"
              value={newTitle}
              placeholder={t("CompanyAnalytics.CostTitlePlaceholder")}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
          </FormField>

          {/* Periodic / One-time toggle */}
          <KindToggleRow>
            <KindBtn
              $active={newKind === "period"}
              onClick={() => setNewKind("period")}
              type="button"
            >
              {t("CompanyAnalytics.CostTypePeriodic")}
            </KindBtn>
            <KindBtn
              $active={newKind === "date"}
              onClick={() => setNewKind("date")}
              type="button"
            >
              {t("CompanyAnalytics.CostTypeOneTime")}
            </KindBtn>
          </KindToggleRow>

          <FormRow>
            <FormField>
              <FormLabel>{t("CompanyAnalytics.CostAmount")}</FormLabel>
              <FormInput
                type="number"
                value={newAmount}
                placeholder="0"
                min="0"
                onChange={(e) => setNewAmount(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </FormField>
            {newKind === "period" ? (
              <FormField>
                <FormLabel>{t("CompanyAnalytics.CostPeriod")}</FormLabel>
                <PeriodSelect
                  value={newPeriod}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "day" || v === "week" || v === "month")
                      setNewPeriod(v);
                  }}
                >
                  <option value="day">{t("CompanyAnalytics.PerDay")}</option>
                  <option value="week">{t("CompanyAnalytics.PerWeek")}</option>
                  <option value="month">
                    {t("CompanyAnalytics.PerMonth")}
                  </option>
                </PeriodSelect>
              </FormField>
            ) : (
              <FormField>
                <FormLabel>{t("CompanyAnalytics.CostDate")}</FormLabel>
                <DatePickerWrapper>
                  <DatePicker
                    value={newDate}
                    onChange={(date) => {
                      if (date) setNewDate(date);
                    }}
                    size="compact"
                    dateFormat="DD/MM/YYYY"
                  />
                </DatePickerWrapper>
              </FormField>
            )}
          </FormRow>
          <AddButtonRow>
            <SecondaryButton
              label={t("CompanyAnalytics.AddCost")}
              onClick={handleAdd}
            />
          </AddButtonRow>
        </AddSection>

        <PaneFooter>
          <SecondaryButton
            label={t("CompanyAnalytics.Cancel")}
            onClick={props.onClose}
          />
          <PrimaryButton
            label={t("CompanyAnalytics.Save")}
            onClick={handleSave}
          />
        </PaneFooter>
      </Pane>
    </>,
    document.body,
  );
};
